<script setup>
import {computed} from 'vue'
import {ChevronRight, MapPin, MapPinOff} from 'lucide-vue-next'

import {Button} from '@/components/ui/button'
import ProjectListItem from './ProjectListItem.vue'
import {useProjectPins} from '@/composables/useProjectPins'

const props = defineProps({
  branch: { type: Object, required: true },
  openKeys: { type: Object, required: true },
  forceOpen: Boolean,
  joinableIds: { type: Array, default: () => [] },
})
const emit = defineEmits(['toggle', 'join'])

const isOpen = computed(() => props.forceOpen || !!props.openKeys[props.branch.key])
const label = computed(() => `${isOpen.value ? 'Collapse' : 'Expand'} ${props.branch.name}`)

// Location browsing ignores parent_id, so sub-projects are listed flat under their Place.
const flat = (project) => ({ ...project, children: [] })

const {pinnedFirst} = useProjectPins()
const projects = computed(() => pinnedFirst(props.branch.projects))
</script>

<template>
  <div class="flex items-center px-0.5">
    <div class="flex flex-1 items-center rounded-lg p-0.5 hover:bg-secondary">
      <Button
        variant="ghostDark"
        class="w-7 h-7 rounded-full p-0 text-muted-foreground hover:text-black"
        :class="{'rotate-90 text-black': isOpen}"
        :aria-label="label"
        :aria-expanded="isOpen"
        :title="label"
        type="button"
        @click="emit('toggle', branch.key)"
      >
        <ChevronRight class="w-7 h-5" aria-hidden="true" />
      </Button>
      <button
        type="button"
        class="flex min-w-0 flex-1 items-center p-0.5 text-sm font-medium"
        @click="emit('toggle', branch.key)"
      >
        <span class="w-5 h-5 mr-2 flex shrink-0 items-center justify-center">
          <MapPinOff v-if="branch.unlocated" class="w-4.5 h-4.5" aria-hidden="true" />
          <MapPin v-else class="w-4.5 h-4.5" aria-hidden="true" />
        </span>
        <span class="truncate" :title="branch.name">{{ branch.name }}</span>
      </button>
      <span class="px-2 text-xs text-muted-foreground">{{ branch.count }}</span>
    </div>
  </div>

  <div v-if="isOpen" class="pl-4 pr-1" role="group" :aria-label="`Projects in ${branch.name}`">
    <PlaceTreeBranch
      v-for="child in branch.children"
      :key="child.key"
      :branch="child"
      :open-keys="openKeys"
      :force-open="forceOpen"
      :joinable-ids="joinableIds"
      @toggle="emit('toggle', $event)"
      @join="emit('join', $event)"
    />
    <ProjectListItem
      v-for="project in projects"
      :key="project.project_id"
      :project="flat(project)"
      :opened-project-ids="{}"
      :joinable="joinableIds.includes(project.project_id)"
      @join="emit('join', $event)"
    />
  </div>
</template>
